// The lesson's record card (specs/scoreboard.md): three numbers and a three-star rating. It opens
// in two places: by tapping the reward target at the end of the menu's progress bar, and by itself
// at the end of a game — over the summary screen when the last heart goes (SummaryScene), and over
// the menu when the child backs out of a round instead (MenuScene).
//
// A game is the whole run — play until the last heart is gone; a round is one board inside it. The
// card is the game's closing beat, so it dismisses like one: a tap ANYWHERE, panel included, sends
// it away, and the "Weiter" button is the visible affordance for that rather than a ✕ to aim at.
//
// Rendered in the #overlay HTML layer like the choice pickers — above the Phaser canvas AND above
// Phaser's DOM layer, so the menu pills cannot float over it — and with the device Back gesture
// wired to close it. It reuses the picker's panel chrome (.picker, .picker__panel) and adds only
// what a record card needs on top.
import i18next from 'i18next';
import * as overlay from './overlay';
import { lessonImage } from './lessonPicker';
import { lessonProgress } from '@/game/progression/lessonStats';
import {
  lessonScoreboard, ratingFor, hasRecords, markScoreboardSeen, RATING_STARS,
  type Scoreboard, type Rating,
} from '@/game/progression/scoreboard';
import { STAR_COUNT } from '@/game/progression/config';
import { starGlyphsFor } from '@/game/starGlyphs';
import { MILESTONE_ART, milestoneReached, type MilestoneArt } from '@/game/milestones';
import type { Pack } from '@/data/packs/types';

const STAR_FULL = '★';
const STAR_EMPTY = '☆';

/**
 * Edge length, in device pixels, of the planet picture the caller is asked to draw. The cell it
 * goes in is at most 48 CSS px (`.score__planet` in index.html), so this is 3x for a phone.
 */
export const PLANET_PX = 144;

/**
 * Draws the rung `name` and hands back an image URL, or null if it cannot. Supplied by MenuScene,
 * which has a Phaser scene to draw with — see `PlanetStack.portrait`.
 */
export type PlanetImage = (name: string) => string | null;

/**
 * Taps that land within this long of the card opening are ignored. Any tap closes the card, and at
 * the end of a game it appears under a child who has spent the last minute hammering the screen —
 * without the guard, one trailing tap of the round they just lost would blank the card before they
 * ever saw it.
 */
const TAP_GUARD_MS = 400;

let popHandler: ((e: PopStateEvent) => void) | null = null;
let open = false;
let openedAt = 0;
/** Runs after a close the PLAYER made (tap, button, Back) — see openScoreboard's `onClose`. */
let onCloseCb: (() => void) | null = null;

/**
 * Closes the panel if one is open. MenuScene calls this on every create(), for the same reason the
 * choice picker does: a language switch restarts the scene once its fonts have loaded, and a panel
 * opened from the outgoing scene would otherwise linger with the old language's numbers.
 *
 * A teardown, not a dismissal: `onClose` does NOT run. The scene calling this is going away, and
 * the summary's star rain must not start on a screen that no longer exists.
 */
export function closeScoreboard(): void {
  if (open) close({ fromPop: false, silent: true });
}

/** Escapes text interpolated into the panel's HTML (lesson titles are data, not markup). */
function esc(s: string): string {
  return s.replace(/[&<>"]/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] ?? c
  ));
}

/** `4130` → `4.1 s`. One decimal: hundredths are noise on a board that takes seconds. */
function seconds(ms: number): string {
  return i18next.t('scoreboard.seconds', { n: (ms / 1000).toFixed(1) });
}

/** `value` is plain text (escaped); `markup` is a drawn value — the planet disc — added after it. */
function statRow(icon: string, label: string, value: string, markup = ''): string {
  return `
    <div class="score__row">
      <span class="score__icon" aria-hidden="true">${icon}</span>
      <span class="score__label">${esc(label)}</span>
      <span class="score__value">${value ? `<span class="score__value-text">${esc(value)}</span>` : ''}${markup}</span>
    </div>`;
}

/**
 * The furthest body a game of this lesson has reached, as the game's own disc and NOTHING else —
 * no round number, no name (specs/scoreboard.md §2.1). The child recognises Saturn's ring on sight;
 * spelling it out under the picture is the caption on a photograph of a cat.
 *
 * The name is still carried, as the image's accessible name and its hover title — a picture with no
 * text is only friendly to the people who can see it.
 *
 * Empty before the first star of any game: there is no body at zero, and inventing one ("noch kein
 * Planet") would put a row of apology where a reward belongs.
 *
 * `planetImage` is the game's OWN drawing of that body, rendered by the caller (MenuScene has the
 * Phaser scene this panel does not). It is what the child watched fill the column a minute ago,
 * down to Jupiter's storm and the ring shadow on Saturn, and the SVG below is only what is left
 * when there is no scene to draw with — a disc and one blob, which is the same picture for ten of
 * the fourteen rungs.
 */
function planetDisc(stackStars: number, planetImage?: PlanetImage): string {
  const name = milestoneReached(stackStars);
  const art = name ? MILESTONE_ART.get(name) : undefined;
  if (!name || !art) return `<span class="score__value-text">${esc(i18next.t('scoreboard.empty'))}</span>`;
  const label = esc(i18next.t(`scoreboard.planets.${name}`));

  const drawn = planetImage?.(name);
  if (drawn) {
    return `<img class="score__planet" src="${esc(drawn)}" alt="${label}" title="${label}"
      width="${PLANET_PX}" height="${PLANET_PX}" draggable="false">`;
  }

  return `
    <svg class="score__planet" viewBox="0 0 24 24" role="img" aria-label="${label}">
      <title>${label}</title>
      ${art.ring ? RING_BACK(art.fill) : ''}
      ${planetBody(name, art)}
      ${art.ring ? RING_FRONT(art.fill) : ''}
    </svg>`;
}

/**
 * Saturn's ring, in the two passes that make it a ring rather than a hoop: the whole ellipse first
 * (the disc then hides its far half), and the near half again ON TOP of the disc. The second,
 * thinner ellipse inside it is the Cassini division — at this size the gap between two strokes
 * reads as the division far better than a dark line would.
 *
 * Keyed on `art.ring`, not on the body's name, so a second ringed body would inherit it.
 */
const RING_BACK = (fill: string): string => `
  <g transform="rotate(-18 12 12)" fill="none" stroke="${fill}">
    <ellipse cx="12" cy="12" rx="11" ry="3.2" stroke-width="1.5" opacity="0.9"/>
    <ellipse cx="12" cy="12" rx="8.6" ry="2.5" stroke-width="1" opacity="0.5"/>
  </g>`;
const RING_FRONT = (fill: string): string => `
  <g transform="rotate(-18 12 12)" fill="none" stroke="${fill}" stroke-linecap="round">
    <path d="M1 12A11 3.2 0 0 0 23 12" stroke-width="1.5" opacity="0.95"/>
    <path d="M3.4 12A8.6 2.5 0 0 0 20.6 12" stroke-width="1" opacity="0.55"/>
  </g>`;

/**
 * The body itself. Ten of the fourteen rungs are a disc with one offset blob, which is all a 31px
 * picture of Mercury or Neptune can carry anyway — but four of them are not planets at all, and as
 * generic discs they were unrecognisable next to the column the child had just watched:
 *
 * - **galaxy** — the last rung in the game, and it looked like a purple ball. It is a tilted spiral:
 *   two arms, blue-white outward and warm inward, around a bright core.
 * - **supernova** — nested shells in the colours the game paints them (hydrogen red, violet mid,
 *   oxygen teal) around a blue-hot core, instead of a blue ball with a red spot.
 * - **eclipse** — the point of the rung is a moon crossing the Sun, so the dark disc has to overlap
 *   its edge; the generic blob sat politely inside it.
 *
 * Everything here is geometry, not assets, and the colours still come from MILESTONE_ART, which
 * `milestones.test.ts` holds against PlanetStack's own palette.
 */
function planetBody(name: string, art: MilestoneArt): string {
  const halo = art.halo ? `<circle cx="12" cy="12" r="11.4" fill="${art.halo}" opacity="0.32"/>` : '';
  switch (name) {
    case 'galaxy':
      return `
        <g transform="rotate(-20 12 12) matrix(1 0 0 0.85 0 1.8)">
          <circle cx="12" cy="12" r="10.6" fill="${art.halo}" opacity="0.26"/>
          <g fill="none" stroke-linecap="round">
            <path d="M12 6.2A6 6 0 0 1 17.6 14.6" stroke="${art.fill}" stroke-width="2.6" opacity="0.95"/>
            <path d="M12 17.8A6 6 0 0 1 6.4 9.4" stroke="${art.fill}" stroke-width="2.6" opacity="0.95"/>
            <path d="M12 8.9A3.1 3.1 0 0 1 14.9 13.4" stroke="#ffe6b8" stroke-width="1.8" opacity="0.9"/>
            <path d="M12 15.1A3.1 3.1 0 0 1 9.1 10.6" stroke="#ffe6b8" stroke-width="1.8" opacity="0.9"/>
          </g>
          <circle cx="12" cy="12" r="2.5" fill="${art.detail}" opacity="0.95"/>
          <circle cx="12" cy="12" r="1.3" fill="#fffdf4"/>
        </g>`;
    case 'supernova':
      return `
        <circle cx="12" cy="12" r="11.2" fill="${art.halo}" opacity="0.3"/>
        <circle cx="10.4" cy="12.8" r="8.4" fill="${art.detail}" opacity="0.4"/>
        <circle cx="13.8" cy="10.6" r="6.8" fill="#1f6f86" opacity="0.45"/>
        <circle cx="12" cy="12" r="3.2" fill="${art.fill}"/>
        <circle cx="11.2" cy="11.2" r="1.4" fill="#ffffff" opacity="0.9"/>`;
    case 'eclipse':
      return `
        ${halo}
        <circle cx="12" cy="12" r="7.6" fill="${art.fill}"/>
        <circle cx="14.9" cy="9.5" r="6.6" fill="${art.detail}"/>`;
    default:
      return `
        ${halo}
        <circle cx="12" cy="12" r="7.6" fill="${art.fill}"/>
        ${art.detail ? `<circle cx="9.6" cy="10" r="3.1" fill="${art.detail}" opacity="0.85"/>` : ''}`;
  }
}

/**
 * The minigame tally: how many UFOs, Diamonds, Ice Cubes and Galaxies this lesson has ever
 * unlocked. Icons and numbers only — the treats are the things a child recognises fastest in the
 * whole game, and a word next to each would say less than the picture does (specs/scoreboard.md
 * §2.2). The 🌌 is last because it is the rarest and reads as the end of the row.
 */
function unlockLine(sb: Scoreboard): string {
  const chip = (icon: string, n: number, key: string) => `
    <span class="score__unlock" title="${esc(i18next.t(key))}">
      <span class="score__unlock-icon" aria-hidden="true">${icon}</span>
      <span class="score__unlock-n">${n}</span>
    </span>`;
  return `
    <div class="score__unlocks" role="group" aria-label="${esc(i18next.t('scoreboard.unlocks'))}">
      ${chip('👽', sb.ufos, 'survival.play')}
      ${chip('💎', sb.diamonds, 'pairs.play')}
      ${chip('🧊', sb.cubes, 'maze.play')}
      ${chip('🌌', sb.galaxies, 'wagon.play')}
      ${chip('🛰️', sb.satellites, 'labyrinth.play')}
      ${chip('🍝', sb.plates, 'recipe.play')}
    </div>`;
}

/**
 * One rating star and its caption. `pos` is 0/1/2 and only positions the star on the arc — the
 * outer two sit a little lower and lean outwards, the middle one rides the top. The caption stays
 * upright underneath whatever the glyph does.
 */
function ratingStar(pos: number, earned: boolean, caption: string): string {
  return `
    <div class="score__star score__star--${pos}${earned ? ' score__star--on' : ''}">
      <span class="score__star-glyph" aria-hidden="true">${earned ? STAR_FULL : STAR_EMPTY}</span>
      <span class="score__star-caption">${esc(caption)}</span>
    </div>`;
}

function panelHtml(
  pack: Pack, sb: Scoreboard, rating: Rating, planetImage?: PlanetImage,
): string {
  const rows = [
    statRow('⏱', i18next.t('scoreboard.bestRound'),
      sb.bestRoundMs !== null ? seconds(sb.bestRoundMs) : i18next.t('scoreboard.empty')),
    statRow('🚀', i18next.t('scoreboard.planetReached'), '',
      planetDisc(sb.bestStackStars, planetImage)),
    statRow('🎯', i18next.t('scoreboard.totalRounds'), String(sb.totalRounds)),
  ].join('');

  const stars = [
    ratingStar(0, rating.fast, i18next.t('scoreboard.star.fast')),
    ratingStar(1, rating.galaxy, i18next.t('scoreboard.star.galaxy')),
    ratingStar(2, rating.complete, i18next.t('scoreboard.star.complete')),
  ].join('');

  const progress = lessonProgress(pack);
  const hint = hasRecords(sb)
    ? ''
    : `<p class="score__hint">${esc(i18next.t('scoreboard.noRounds'))}</p>`;

  // One head row, not two: in a phone's landscape viewport vertical space is the scarce dimension,
  // so the lesson's art, the panel title, the lesson name and its journey stars share a line.
  return `
    <div class="picker" id="score-scrim">
      <div class="picker__panel score__panel" role="dialog" aria-label="${esc(i18next.t('scoreboard.title'))}">
        <div class="score__head">
          <img class="score__art" src="${esc(lessonImage(pack))}" alt="" draggable="false">
          <span class="score__head-text">
            <span class="score__title">${esc(i18next.t('scoreboard.title'))}</span>
            <span class="score__lesson">${esc(pack.title)}
              <span class="score__lesson-stars">${starGlyphsFor(pack).full} ${progress.stars}/${STAR_COUNT}</span>
            </span>
          </span>
          <button type="button" id="score-close" class="score__go">${esc(i18next.t('nav.continue'))}</button>
        </div>
        <div class="score__body">
          <div class="score__stats">${rows}${unlockLine(sb)}${hint}</div>
          <!-- No heading over the stars: three stars, some of them gold, need no label saying
               they are a rating. The string survives as the block's accessible name. -->
          <div class="score__stars" role="group"
               aria-label="${esc(i18next.t('scoreboard.rating'))} ${rating.stars}/${RATING_STARS}">
            ${stars}
          </div>
        </div>
      </div>
    </div>`;
}

/**
 * Opens the record card for `pack` — the active lesson, whose reward icon was tapped, or the lesson
 * the finished game was played in.
 *
 * `planetImage` draws the reached body; without it the card falls back to its own plain SVG.
 * `onClose` runs once the player dismisses it, so a caller can hold back whatever it wants seen
 * after the card rather than behind it.
 */
export function openScoreboard(
  pack: Pack, planetImage?: PlanetImage, opts: { onClose?: () => void } = {},
): void {
  const sb = lessonScoreboard(pack);
  overlay.show(panelHtml(pack, sb, ratingFor(sb), planetImage));
  // Marked read HERE, on open, and not on close: what the menu's pulsing target asks is whether
  // these numbers have been in front of the child, and they now have. Every entry point goes
  // through this function, so none of them can forget.
  markScoreboardSeen(pack, sb);
  open = true;
  openedAt = performance.now();
  onCloseCb = opts.onClose ?? null;

  // Hardware/gesture Back closes the panel instead of leaving the app (TWA/WKWebView) — the same
  // contract every other overlay dialog in the game keeps.
  history.pushState({ scoreboard: true }, '');
  popHandler = () => close({ fromPop: true });
  window.addEventListener('popstate', popHandler);

  // One handler for the whole overlay, backdrop and panel alike: nothing on this card is tappable
  // (specs/scoreboard.md §1), so there is nothing for a tap to mean except "done looking".
  // `pointerdown`, not `click`, so it goes the moment the finger lands.
  const scrim = document.getElementById('score-scrim');
  scrim?.addEventListener('pointerdown', (e) => {
    if (performance.now() - openedAt < TAP_GUARD_MS) return;
    // Phaser listens for pointer events on the WINDOW, not just its canvas, so without this the
    // one tap that dismisses the card also reaches the scene underneath — on the summary screen
    // that is a full-screen catcher zone, and the child was thrown to the menu before they could
    // see the screen the card was covering.
    e.stopPropagation();
    close({ fromPop: false });
  });
  // The button's own listener is for the keyboard: Enter/Space fire `click` without ever sending a
  // pointerdown, and the card must stay closable without a touchscreen.
  document.getElementById('score-close')
    ?.addEventListener('click', () => close({ fromPop: false }));
}

// fromPop: the close came from a popstate (Back), so history has already moved and we must not
// call history.back() again. Otherwise pop our own pushed entry.
// silent: a teardown rather than a dismissal — skip `onClose` (see closeScoreboard).
function close({ fromPop, silent = false }: { fromPop: boolean; silent?: boolean }): void {
  if (!open) return;   // the tap handler and the button can both land on one activation
  open = false;
  if (popHandler) {
    window.removeEventListener('popstate', popHandler);
    popHandler = null;
  }
  overlay.hide();
  if (!fromPop && history.state?.scoreboard) history.back();
  const cb = onCloseCb;
  onCloseCb = null;
  if (!silent) cb?.();
}
